/**
 * ANOMALY MARKERS — a point that is far enough off its own recent behaviour to
 * be worth a second look, with a plain-language reason.
 *
 * ── AGAINST A TRAILING BASELINE, NOT AGAINST THE WHOLE SERIES ──
 *
 * A mean over the whole window includes the spike itself, which drags the
 * baseline toward it and makes a big spike look less exceptional than a small
 * one. Trailing means "compared with the days before it", which is the
 * comparison a reader is actually making when they look at a chart.
 *
 * ── THE THREE THINGS IT REFUSES TO FLAG ──
 *
 *   · The first `MIN_BASELINE` points. Two days is not a baseline, and a marker
 *     on day one of a range is noise on every range a reader picks.
 *   · A FLAT OR NEARLY FLAT baseline. With σ = 0 every non-equal point is
 *     infinitely many standard deviations out, so a building that logged exactly
 *     4 entries a day for a week would light up on the day it logged 5. Worse,
 *     σ scales with the numbers: a site running at 100 a day with a spread of
 *     1.3 makes a perfectly ordinary 97 into a 2.3σ event. So a movement has to
 *     clear THREE gates — the sigma threshold, a minimum ABSOLUTE move (which
 *     protects small numbers), and a minimum RELATIVE move (which protects large
 *     ones). Any one of the three alone produces a chart covered in markers.
 *   · The LAST point of a rolling range. Its bucket is a fraction of a day — the
 *     period ends at the instant of the request — so it is always low, and
 *     flagging it would mark the clock as an anomaly every single time. This is
 *     the same fact the pulse panel already prints a caveat about.
 *
 * The reason string is the point. "2.4σ" is a statistic; "3.1× the usual for a
 * Tuesday — 412 entries against a recent average of 132" is something a manager
 * can act on.
 */

export interface AnomalyPoint {
  label: string;
  value: number;
}

export interface Anomaly {
  index: number;
  label: string;
  value: number;
  /** Signed. Positive is a spike, negative is a drop. */
  sigma: number;
  /** The trailing mean it was compared against. */
  baseline: number;
  direction: 'spike' | 'drop';
  /** Plain language, for the tooltip. */
  reason: string;
}

export interface AnomalyOptions {
  /** How many trailing buckets form the baseline. */
  window?: number;
  /** How many buckets are required before anything can be flagged. */
  minBaseline?: number;
  /** Standard deviations off the trailing mean. */
  threshold?: number;
  /** A minimum absolute movement, so a near-flat small series does not light up
   *  on ±1. Protects LOW-volume buildings. */
  minAbsolute?: number;
  /** A minimum movement as a FRACTION of the baseline. Protects HIGH-volume
   *  buildings, where a tight spread turns an ordinary day into a 2σ event: at
   *  100 a day with σ 1.3, a 97 is 2.3 standard deviations out and completely
   *  unremarkable. */
  minRelative?: number;
  /** Skip the final bucket — true for a rolling range whose last day is partial. */
  ignoreLast?: boolean;
}

const DEFAULTS: Required<AnomalyOptions> = {
  window: 7,
  minBaseline: 4,
  threshold: 2,
  minAbsolute: 3,
  minRelative: 0.25,
  ignoreLast: false,
};

const mean = (values: number[]): number =>
  values.reduce((sum, v) => sum + v, 0) / values.length;

const stdDev = (values: number[], avg: number): number =>
  Math.sqrt(values.reduce((sum, v) => sum + (v - avg) ** 2, 0) / values.length);

const times = (value: number, baseline: number): string => {
  if (baseline <= 0) return '';
  const ratio = value / baseline;
  if (ratio >= 1.15) return `${(Math.round(ratio * 10) / 10).toLocaleString()}× the usual`;
  if (ratio <= 0.85) {
    return `${Math.round((1 - ratio) * 100)}% below the usual`;
  }

  return '';
};

export const findAnomalies = (
  points: AnomalyPoint[],
  options: AnomalyOptions = {}
): Anomaly[] => {
  const opts = { ...DEFAULTS, ...options };
  const out: Anomaly[] = [];
  const last = points.length - 1;

  for (let i = opts.minBaseline; i < points.length; i++) {
    if (opts.ignoreLast && i === last) continue;

    const baselineValues = points
      .slice(Math.max(0, i - opts.window), i)
      .map(p => p.value);
    if (baselineValues.length < opts.minBaseline) continue;

    const avg = mean(baselineValues);
    const sd = stdDev(baselineValues, avg);
    const value = points[i].value;
    const move = value - avg;

    // THREE GATES, and each one exists for a different failure.
    //
    // sd === 0     a flat baseline makes every difference infinite.
    // minAbsolute  protects a LOW-volume series: 4 a day going to 5 is not news.
    // minRelative  protects a HIGH-volume one: at 100 a day with a tight spread,
    //              an ordinary 97 is 2.3σ out and means nothing. Without this the
    //              busiest buildings get the most markers, which is exactly
    //              backwards.
    if (sd === 0) continue;
    if (Math.abs(move) < opts.minAbsolute) continue;
    if (avg > 0 && Math.abs(move) / avg < opts.minRelative) continue;

    const sigma = move / sd;
    if (Math.abs(sigma) < opts.threshold) continue;

    const rounded = Math.round(avg * 10) / 10;
    const comparison = times(value, avg);
    const direction: Anomaly['direction'] = move > 0 ? 'spike' : 'drop';

    out.push({
      index: i,
      label: points[i].label,
      value,
      sigma: Math.round(sigma * 10) / 10,
      baseline: rounded,
      direction,
      reason:
        `${comparison ? `${comparison} — ` : ''}` +
        `${value.toLocaleString()} against a recent average of ` +
        `${rounded.toLocaleString()} over the previous ` +
        `${baselineValues.length} ${baselineValues.length === 1 ? 'bucket' : 'buckets'}.`,
    });
  }

  return out;
};

/** Keyed by label, for a chart that has to ask "is THIS point an anomaly". */
export const anomalyIndex = (anomalies: Anomaly[]): Map<string, Anomaly> =>
  new Map(anomalies.map(a => [a.label, a]));
